<script>
    import { onMount, onDestroy } from "svelte";
    import mapboxgl from 'mapbox-gl';

    // Mobile detection
    import UA from "ua-parser-js";
    const ua = new UA();
    let isMobile = ua.getResult().device["type"] == "mobile";

    export let map; // Map instance passed as prop
    export let goto; // [lng, lat] for when clicked
    export let location; // [lng, lat] marker position
    export let text; // text to display on button
    export let iconPosition = 'right'; // position of icon relative to button (up, down, left, right)
    export let iconRotation; // rotation of icon (0-360 degrees)
    export let rotation = 0; // rotation of marker (0-360 degrees)
    export let zoom = 16.75; // zoom level for when clicked
    export let primaryArrowColor = '#ffffff'; // color of marker body
    export let secondaryArrowColor = '#FFD200';  // color of marker arrow
    export let enabled = true;
    export let mobile; // mobile overrides
    
    let marker = null;
    let markerElement = null;
        
    // Apply mobile overrides
    $: if (isMobile && mobile && typeof mobile === 'object') {
        if (mobile.location) location = mobile.location;
        if (mobile.goto) goto = mobile.goto;
        if (mobile.text) text = mobile.text;
        if (mobile.iconPosition) iconPosition = mobile.iconPosition;
        if (mobile.iconRotation !== undefined) iconRotation = mobile.iconRotation;
        if (mobile.rotation !== undefined) rotation = mobile.rotation;
        if (mobile.zoom) zoom = mobile.zoom;
        if (mobile.primaryArrowColor) primaryArrowColor = mobile.primaryArrowColor;
        if (mobile.secondaryArrowColor) secondaryArrowColor = mobile.secondaryArrowColor;
        if (mobile.enabled !== undefined) enabled = mobile.enabled;
    }

    $: lng = location?.[0];
    $: lat = location?.[1];

    const svgHeight = 25;
    const svgWidth = svgHeight * 1.15;

    function goToCampus() {
        if (map && goto) {
            map.flyTo({ center: goto, zoom: zoom });
            console.log("clicked");
        }
    }

    function createMarkerElement() {
        const container = document.createElement('div');
        container.className = 'campus-marker';
        container.style.transform = `rotate(${rotation}deg)`;

        // Create SVG element
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('class', `campus-marker--svg ${iconPosition}`);
        svg.setAttribute('height', svgHeight);
        svg.setAttribute('width', svgWidth);
        svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        svg.setAttribute('version', '1.1');
        svg.setAttribute('viewBox', '0 0 115 100');
        svg.setAttribute('xml:space', 'preserve');
        
        if (iconRotation) {
            svg.style.transform = `translateX(-50%) rotate(${iconRotation}deg)`;
        }

        const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
        defs.setAttribute('id', 'defs16');
        
        const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        g.setAttribute('id', 'g6');
        g.setAttribute('transform', 'translate(-2.3,-10)');

        const path1 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path1.setAttribute('d', 'M 70.2,56.9 34.2,11.6 C 33.3,10.7 31.7,10 30.5,10 H 5.4 c -1.9,0 -3.1,1.2 -3.1,3.1 v 93.4 c 0,1.9 1.2,3.1 3.1,3.1 l 24.5,0.3 c 1.6,0 3.1,-0.6 4,-1.9 L 70.2,62.7 c 1.3,-1.8 1.3,-4.3 0,-5.8 z');
        path1.setAttribute('id', 'path2');
        path1.setAttribute('style', `fill:${primaryArrowColor};fill-opacity:1`);

        const path2 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path2.setAttribute('d', 'M 116.8,56.9 80.5,11.9 C 78.9,10 76.2,9.4 74,11.3 l -7.1,5.9 c -2.2,1.6 -2.5,4.7 -0.6,6.5 L 95.7,60 66.2,96.6 c -1.6,1.9 -1.2,5 0.6,6.5 L 74,109 c 2.2,1.6 4.7,1.2 6.5,-0.6 l 36.3,-45.3 c 1.2,-2.5 1.2,-4.7 0,-6.2 z');
        path2.setAttribute('id', 'path4');
        path2.setAttribute('style', `fill:${secondaryArrowColor};fill-opacity:1`);

        g.appendChild(path1);
        g.appendChild(path2);
        svg.appendChild(defs);
        svg.appendChild(g);

        // Create button element
        const button = document.createElement('div');
        button.className = 'campus-marker--button';
        button.addEventListener('click', goToCampus);

        const textDiv = document.createElement('div');
        textDiv.className = 'campus-marker--text';
        textDiv.textContent = text;
        button.appendChild(textDiv);

        container.appendChild(svg);
        container.appendChild(button);

        return container;
    }

    function updateMarkerElement() {
        if (!markerElement) return;
        
        // Update rotation
        markerElement.style.transform = `rotate(${rotation}deg)`;
        
        // Update SVG icon position class
        const svg = markerElement.querySelector('.campus-marker--svg');
        if (svg) {
            svg.className = `campus-marker--svg ${iconPosition}`;
            if (iconRotation) {
                svg.style.transform = `translateX(-50%) rotate(${iconRotation}deg)`;
            } else {
                svg.style.transform = '';
            }
        }
        
        // Update colors
        const path2 = markerElement.querySelector('#path2');
        const path4 = markerElement.querySelector('#path4');
        if (path2) path2.setAttribute('style', `fill:${primaryArrowColor};fill-opacity:1`);
        if (path4) path4.setAttribute('style', `fill:${secondaryArrowColor};fill-opacity:1`);
        
        // Update text
        const textDiv = markerElement.querySelector('.campus-marker--text');
        if (textDiv) textDiv.textContent = text;
    }

    onMount(() => {
        if (!map) {
            console.warn('Map prop not available for CampusMarker');
            return;
        }

        markerElement = createMarkerElement();
    });

    // Handle marker add/remove based on enabled prop and location
    $: if (map && markerElement && enabled && location && lng !== undefined && lat !== undefined) {
        if (!marker) {
            marker = new mapboxgl.Marker(markerElement)
                .setLngLat([lng, lat])
                .addTo(map);
        } else {
            marker.setLngLat([lng, lat]);
            updateMarkerElement();
        }
    } else if (marker) {
        marker.remove();
        marker = null;
    }

    // Update marker when props change
    $: if (marker && markerElement) {
        updateMarkerElement();
        if (location && lng !== undefined && lat !== undefined) {
            marker.setLngLat([lng, lat]);
        }
    }

    onDestroy(() => {
        if (marker) {
            marker.remove();
            marker = null;
        }
    });
</script>

<style>
    :global(.campus-marker) {
        position: relative;
        text-align: center;
    }

    :global(.campus-marker--button) {
        padding: 0.5em;
        border: 2px solid #fff;
        border-radius: 10px;
        cursor: pointer;
    }

    :global(.campus-marker:hover .campus-marker--button) {
        background: #fff;
    }

    :global(.campus-marker--text),
    :global(.campus-marker--svg) {
        display: inline-block;
        vertical-align: middle;
    }

    :global(.campus-marker--text) {
        color: white;
        font-size: 18px;
        font-weight: 600;
        margin-right: 0.25em;
        font-family: "Nunito Sans", sans-serif;
        text-shadow: 0 0 10px rgb(255 255 255 / 35%);
        line-height: 1.5;
        text-align: center;
    }
    
    :global(.campus-marker:hover .campus-marker--text) {
        color: #000;
    }

    :global(.campus-marker--svg) {
        position: absolute;
    }

    :global(.campus-marker--svg.up) {
        top: -35px;
        left: 50%;
        transform: translateX(-50%) rotate(-90deg);
    }

    :global(.campus-marker--svg.down) {
        bottom: -35px;
        left: 50%;
        transform: translateX(-50%) rotate(90deg);
    }

    :global(.campus-marker--svg.left) {
        transform: rotate(-180deg);
    }

    :global(.campus-marker--svg.right) {
        /* No additional transform needed for right position */
    }
</style>
